/**
 * /news no tema do Cabal Next (dono: a pagina antiga estava com cores/logo/tema do Liberty).
 * Header/rodape da home, faixa de titulo, abas por categoria e grade de cards. Dados: useNewsCards (newsData.ts),
 * a mesma getNews() da pagina antiga (NewsPage.tsx fica no projeto). Tambem e' aberta pelo launcher (/News).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { NextFooter, NextHeader, useReveal, useScrollTopOnMount } from '@/features/Home/next/NextLayout';
import {
  CATEGORY_LABEL,
  NEWS_ASSET,
  NEWS_TABS,
  newsPlainTitle,
  useNewsCards,
  type NewsCard,
  type NewsTab,
} from '@/features/Home/next/newsData';
import { newsImg, newsImgFallback } from '@/shared/utils/newsImg';
import '@/features/Home/next/news.css';
import './news-pages.css';

const PAGE = 9;

export function NewsCardItem({ card, delay = 0 }: { card: NewsCard; delay?: number }) {
  const navigate = useNavigate();
  const clickable = !!card.slug;
  const body = (
    <>
      <div className="nxp-card__img">
        <img src={newsImg(card.image, 640)} onError={newsImgFallback(card.image)} alt={card.title} loading="lazy" />
      </div>
      <div className="nxp-card__body">
        <span className="nxn-meta">
          <span className={`nxn-chip nxn-chip--${card.category}`}>{CATEGORY_LABEL[card.category]}</span>
          <span className="nxn-chip nxn-chip--date">{card.date}</span>
        </span>
        <h3>{newsPlainTitle(card.title)}</h3>
        <p>{card.text}</p>
        {clickable && (
          <span className="nxp-card__more">
            Ler mais <ArrowRight size={15} />
          </span>
        )}
      </div>
    </>
  );
  return (
    <article className="nxp-card" data-reveal style={{ '--d': `${delay}s` } as CSSProperties}>
      {clickable ? (
        <button type="button" onClick={() => navigate(`/news/${card.slug}`)}>
          {body}
        </button>
      ) : (
        <div>{body}</div>
      )}
    </article>
  );
}

export function NewsCardSkeleton() {
  return (
    <article className="nxp-card nxp-card--skeleton" aria-hidden="true">
      <div className="nxp-card__img nx-skeleton" />
      <div className="nxp-card__body">
        <span className="nx-skeleton" />
        <span className="nx-skeleton" />
        <span className="nx-skeleton" />
      </div>
    </article>
  );
}

export default function NextNewsPage() {
  const rootRef = useRef<HTMLDivElement>(null);
  const { cards, loading } = useNewsCards();
  const [tab, setTab] = useState<NewsTab>('todas');
  const [shown, setShown] = useState(PAGE);
  useScrollTopOnMount();
  useEffect(() => {
    document.title = 'Notícias · Cabal Next';
  }, []);

  const filtered = useMemo(() => (tab === 'todas' ? cards : cards.filter((c) => c.category === tab)), [cards, tab]);
  const visible = filtered.slice(0, shown);
  useReveal(rootRef, [loading, tab, shown]);

  return (
    <div ref={rootRef} className="nx">
      <NextHeader active="noticias" />
      <main>
        <section className="nxp-hero" style={{ backgroundImage: `url(${NEWS_ASSET}/news-bg.webp)` }}>
          <div className="nx-container">
            <p className="nx-eyebrow nx-in">Cabal Next</p>
            <h1 className="nxp-title nx-in" style={{ '--d': '0.1s' } as CSSProperties}>
              Notícias
            </h1>
            <p className="nxp-subtitle nx-in" style={{ '--d': '0.2s' } as CSSProperties}>
              Atualizações, patch notes e eventos do Cabal Next em primeira mão.
            </p>
          </div>
        </section>

        <section className="nxp-body">
          <div className="nx-container">
            <div className="nxn-tabs nxp-tabs" role="tablist">
              {NEWS_TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.id}
                  className={tab === t.id ? 'is-active' : undefined}
                  onClick={() => {
                    setTab(t.id);
                    setShown(PAGE);
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="nxp-grid">
                {Array.from({ length: 6 }, (_, i) => (
                  <NewsCardSkeleton key={i} />
                ))}
              </div>
            ) : visible.length ? (
              <div className="nxp-grid">
                {visible.map((c, i) => (
                  <NewsCardItem key={c.slug ?? `fb-${i}`} card={c} delay={(i % 3) * 0.08} />
                ))}
              </div>
            ) : (
              <p className="nxp-empty">Nenhuma notícia nesta categoria ainda.</p>
            )}

            {!loading && filtered.length > shown && (
              <button type="button" className="nxp-more" onClick={() => setShown((n) => n + PAGE)}>
                Carregar mais
              </button>
            )}
          </div>
        </section>
      </main>
      <NextFooter />
    </div>
  );
}
